/**
 * The auth writes for ONE WLAN, in the data model AND THE VOCABULARY its own
 * device speaks.
 *
 * This is the platform's single definition of "make this network WPA2" and
 * "make this network open", and it lives here because both the ONU page's WiFi
 * editor and the bulk SSID action send it. Two copies of a vendor-facing write
 * is how the same radio ends up configured two different ways depending on
 * which screen an operator used.
 *
 * TR-098 spreads auth over `BeaconType` and its per-suite sub-parameters:
 * "11i" alone leaves whatever authentication mode the CPE last held, so WPA2
 * states all three. TR-181 says the whole thing in `Security.ModeEnabled`.
 *
 * OPEN IS NOT ONE WORD, and assuming it was cost a live write. TR-098 has two
 * spellings for a network with no authentication — `None`, and the pre-WPA
 * `Basic` with both Basic sub-modes `None` — and the vendors here do not
 * accept each other's:
 *
 *   ZTE F670L      `None`.  `Basic` is REFUSED: a real write of
 *                  BeaconType=Basic + BasicAuthenticationMode=None came back
 *                  `cwmp.9003 Invalid arguments` on Connect - Bonyen
 *                  (2026-09-24), and `None` then took on the same two slots.
 *                  No F670L on this fleet reports `Basic` on any WLAN.
 *   Huawei HG8546M `Basic` + both Basic sub-modes, live-verified 2026-09-09.
 *                  It reports both spellings — 133 instances say `None`, 2 say
 *                  `Basic`.
 *
 * So the value is taken from WHAT THE DEVICE ITSELF REPORTS rather than from a
 * vendor table: `observed` is every BeaconType the CPE currently has across its
 * WLANs. A device that shows `None` is written `None`; one that shows only
 * `Basic` gets the Basic form it is evidently using; a device that shows
 * neither gets `None`, which is the standard's own word and what 106 of the
 * 111 instances carrying this fleet's public network report.
 */
export interface WlanSecurityWrite {
  path: string;
  value: string;
}

export type WlanSecurityChoice = 'wpa2' | 'open';

/** The TR-098 spellings of "no authentication", most-standard first. */
const OPEN_BEACON_NONE = 'None';
const OPEN_BEACON_BASIC = 'Basic';

/**
 * Which spelling of "open" THIS device uses, from what it reports.
 *
 * Null when it reports nothing recognisable, and the caller then sends the
 * standard's own word rather than guessing at a dialect.
 */
export function openBeaconTypeFor(
  observed: readonly (string | null | undefined)[] | undefined,
): string {
  const seen = (observed ?? [])
    .filter((v): v is string => typeof v === 'string' && v.trim() !== '')
    .map((v) => v.trim());
  if (seen.some((v) => v.toLowerCase() === OPEN_BEACON_NONE.toLowerCase())) return OPEN_BEACON_NONE;
  if (seen.some((v) => v.toLowerCase() === OPEN_BEACON_BASIC.toLowerCase())) return OPEN_BEACON_BASIC;
  return OPEN_BEACON_NONE;
}

export function wlanSecurityWrites(
  securityPath: string | null,
  choice: WlanSecurityChoice,
  /** Every `BeaconType` this CPE reports across its WLANs. See above. */
  observed?: readonly (string | null | undefined)[],
): WlanSecurityWrite[] {
  if (!securityPath) return [];
  if (securityPath.endsWith('.BeaconType')) {
    const base = securityPath.replace(/\.BeaconType$/, '');
    if (choice === 'wpa2') {
      return [
        { path: securityPath, value: '11i' },
        { path: `${base}.IEEE11iAuthenticationMode`, value: 'PSKAuthentication' },
        { path: `${base}.IEEE11iEncryptionModes`, value: 'AESEncryption' },
      ];
    }
    const beacon = openBeaconTypeFor(observed);
    /**
     * `Basic` needs its sub-modes stated — the beacon alone would leave
     * whatever encryption was there. `None` is the whole statement, and the
     * F670L that refused the longer form took exactly this.
     */
    return beacon === OPEN_BEACON_BASIC
      ? [
          { path: securityPath, value: OPEN_BEACON_BASIC },
          { path: `${base}.BasicAuthenticationMode`, value: 'None' },
          { path: `${base}.BasicEncryptionModes`, value: 'None' },
        ]
      : [{ path: securityPath, value: OPEN_BEACON_NONE }];
  }
  return [{ path: securityPath, value: choice === 'wpa2' ? 'WPA2-Personal' : 'None' }];
}
